'use client';

import React, { useState } from "react";
import { Settings2 } from "lucide-react";
import { OperatorDesktop } from "./OperatorDesktop";
import { OperatorPhone } from "./OperatorPhone";
import { OwnerPhone } from "./OwnerPhone";
import { KretaFrame } from "./KretaFrame";
import { ManageSourcesSheet } from "./settings";
import { INSTALL_PARAMS } from "./config";

// Modes: operator-desktop, operator-phone (behind install flag), owner-phone, kreta-preview.
// Operator · Phone is hidden for the pilot via enableOperatorPhone (11 Jun 2026);
// the code stays for hosts that want it.
const TABS = [
  { key: "operator-desktop", label: "Operator · Desktop" },
  ...(INSTALL_PARAMS.enableOperatorPhone
    ? [{ key: "operator-phone", label: "Operator · Phone" }]
    : []),
  { key: "owner-phone", label: "Owner · Phone" },
  { key: "kreta-preview", label: "Inside Kreta · Preview" },
];

export const Shell = () => {
  const [mode, setMode] = useState(
    typeof window !== "undefined" && window.innerWidth < 768 && INSTALL_PARAMS.enableOperatorPhone
      ? "operator-phone"
      : "operator-desktop"
  );
  const [settingsOpen, setSettingsOpen] = useState(false);

  return (
    <div className="min-h-screen bg-zinc-50 font-sans text-zinc-900" style={{ fontFamily: "'Hind', 'IBM Plex Sans', system-ui, sans-serif" }}>
      {/* Top DEMO banner */}
      <div
        data-testid="demo-banner"
        className="sticky top-0 z-20 flex items-center justify-between gap-4 bg-zinc-900 px-4 py-2 text-xs text-zinc-100"
      >
        <div className="flex items-center gap-2">
          <span className="inline-flex h-2 w-2 animate-pulse rounded-full bg-amber-400" />
          <span className="font-semibold uppercase tracking-wider">Demo mode</span>
          <span className="hidden text-zinc-300 sm:inline">— refresh wipes everything. Sample data only.</span>
        </div>
        <span className="font-semibold tracking-tight">Jigyasa</span>
      </div>

      {/* View switcher */}
      <div
        data-testid="view-switcher"
        className="sticky top-9 z-10 flex items-center gap-1 overflow-x-auto border-b border-zinc-200 bg-white px-3 py-2"
      >
        {TABS.map((t) => {
          const active = mode === t.key;
          return (
            <button
              key={t.key}
              data-testid={`view-switch-${t.key}`}
              onClick={() => setMode(t.key)}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                active
                  ? "bg-zinc-900 text-white"
                  : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
              }`}
            >
              {t.label}
            </button>
          );
        })}
        <button
          data-testid="open-settings-btn"
          onClick={() => setSettingsOpen(true)}
          className="ml-auto inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-100"
        >
          <Settings2 size={13} /> Sources
        </button>
      </div>

      {/* Phone-frame wrapper when in a phone view on desktop screens */}
      {mode === "operator-desktop" && <OperatorDesktop />}
      {mode === "operator-phone" && <PhoneFrame><OperatorPhone /></PhoneFrame>}
      {mode === "owner-phone" && <PhoneFrame><OwnerPhone /></PhoneFrame>}
      {mode === "kreta-preview" && <KretaFrame />}

      <ManageSourcesSheet open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  );
};

const PhoneFrame = ({ children }) => (
  <div className="flex justify-center px-2 py-4 sm:py-8">
    <div
      data-testid="phone-frame"
      className="w-full max-w-md overflow-hidden rounded-[28px] border border-zinc-300 bg-zinc-50 shadow-md sm:max-w-[420px]"
      style={{ minHeight: "calc(100vh - 140px)" }}
    >
      {children}
    </div>
  </div>
);
